import axios from "axios";
import { API } from "../../../config";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";

import { setCreatedGroupValues } from "../../slices/fleet/locations";
import {
  CreateNewSiteSetValues,
  setActionValues,
  setCreateLocationStep,
  setErrorCreateLoc,
  setMultiLocationLoading,
  setMultiLocationSuccess,
  setMultiLocError,
  setMultiLocsCreated,
  setUsersCreated,
} from "../../slices/location/createLocation";
import { ScenarioData } from "../actions/actionTypes";
import { moveRemoveAddLocToUser } from "../users/userAsyncActions";
import { moveScenarioToGroup } from "../actions/actionsAsyncActions";
import { setVerifyAllIsCreated } from "../../slices/users/createUsers";
import { getLocalizedTextByLocaleVersionTwo } from "../../../utils/localized-text";
import { OrgGeoPointData } from "../locations/locationsTypes";

export const generatePassword = () => {
  let length = 8,
    charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
    retVal = "";
  for (var i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
};

export const fetchLocationMapboxCoordinates = createAsyncThunk(
  "CreateLocationReducer/fetchLocationMapbox",
  async (arg: { longitude: number; latitude: number }, store) => {
    const { createNewSite, createLocationStep } = (
      store.getState() as RootState
    ).CreateLocationReducer;

    const { locale } = (store.getState() as RootState).LayoutReducer;

    try {
      const response = await axios.get(
        `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${arg.longitude}&latitude=${arg.latitude}&access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
        {
          headers: {
            "Accept-Language": `${locale}`,
          },
        }
      );
      const { data } = response;
      const values = data.features[0];
      if (values) {
        store.dispatch(
          CreateNewSiteSetValues({
            ...createNewSite,
            siteAddress: values.properties.full_address,
            countryName: values.properties.context.country.name,
            longitude: values.properties.coordinates.longitude,
            latitude: values.properties.coordinates.latitude,
          })
        );
        store.dispatch(setErrorCreateLoc(""));
      } else {
        store.dispatch(
          setCreateLocationStep({
            ...createLocationStep,
            step: 1,
            locationValidate: false,
          })
        );
      }
    } catch (error: any) {
      store.dispatch(setErrorCreateLoc(error.response.data.message));
      store.dispatch(
        setCreateLocationStep({
          ...createLocationStep,
          step: 1,
          locationValidate: false,
        })
      );
      store.rejectWithValue(error.response.data.message);
    }
  }
);

export const fetchActionsNamesUsersConfig = createAsyncThunk(
  "CreateLocationReducer/fetchActionsNamesUsersConfig",
  async (token: string, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;

    try {
      const response = await axios.get(
        `${API}/core/scenario?isSystemScenario=false&deletedAt=null&statusConstantId[$in][]=34&statusConstantId[$in][]=35&$eager=[scopes.[group],cerealType.text.[localizedTexts.[locale]], statusConstant, name.[localizedTexts.[locale]], desc.[localizedTexts.[locale]]]&$limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": `${locale}`,
          },
        }
      );

      const { data } = response;

      const actionsValues = data.data.map((row: ScenarioData) => {
        const scenarioNameValue = () => {
          if (row.name) {
            let def = "";
            const arr = row.name.localizedTexts
              .map((c: any) => {
                if (locale === c.locale.value) {
                  return c.translation;
                }
                def = c.translation;
              })
              .filter((val: any) => null != val);
            if (!arr.length) {
              return [def].toString();
            } else {
              return arr.toString();
            }
          } else {
            return "Null";
          }
        };

        const tableRows = {
          name: scenarioNameValue().toLowerCase() as string,
          id: row.id,
          cerealType: getLocalizedTextByLocaleVersionTwo(
            locale,
            row.cerealType.text.localizedTexts
          ),
        };
        return tableRows;
      });

      store.dispatch(setActionValues(actionsValues));
      return data.data;
    } catch (error) {
      return error;
    }
  }
);

export const userCreation = createAsyncThunk(
  "CreateLocationReducer/userCreation",
  async (
    arg: {
      adminId: string;
      token: string;
      username: string;
      password: string;
      associatedGeoPointId: number;
    },
    store
  ) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;

    const headers = {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${arg.token}`,
    };

    try {
      const response = await axios.post(
        `${API}/users/register`,
        {
          username: arg.username,
          password: arg.password,
          userType: "site",
          email: "",
          remoteId: "",
          firstname: "",
          lastname: "",
          associatedGeoPointId: arg.associatedGeoPointId,
          administratorId: arg.adminId,
        },
        { headers }
      );

      const { data } = response;
      return data;
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  }
);

export const createLocation = createAsyncThunk(
  "CreateLocationReducer/createLocation",
  async (arg: { adminId: string; token: string; realmId: number }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { createNewSite, rowsAlpha } = (store.getState() as RootState)
      .CreateLocationReducer;

    const { verifyIfAllIsCreated } = (store.getState() as RootState)
      .CreateUsersReducer;

    const { siteName, siteAddress, longitude, latitude, countryName } =
      createNewSite;

    const headers = {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${arg.token}`,
    };

    store.dispatch(setMultiLocationLoading(true));
    try {
      const response = await axios.post(
        `${API}/users/organization-geo-point`,
        {
          name: siteName,
          orgId: arg.realmId,
          typeId: 1,
          latitude,
          longitude,
          address: siteAddress,
          altitude: 0,
          countryName,
        },
        { headers }
      );
      const { data } = response;

      const usersPromises = rowsAlpha.map((usersConfigs) => {
        const manageSiteAssign = () => {
          if (usersConfigs.assignedSite.id === "upcomingSite") {
            return data.id;
          }

          if (usersConfigs.assignedSite.id === "nomad") {
            return undefined;
          } else {
            return usersConfigs.assignedSite.id;
          }
        };

        return store.dispatch(
          userCreation({
            token: arg.token,
            username: usersConfigs.username,
            password: generatePassword(),
            associatedGeoPointId: manageSiteAssign(),
            adminId: arg.adminId,
          })
        );
      });

      const createdUsers = await Promise.all(usersPromises);
      const res = createdUsers.map((v) => v.payload);
      const rowsAlphaTransform = rowsAlpha.reduce((arr, e) => {
        arr.push(
          Object.assign(
            {},
            e,
            res.find((a) => a.username === e.username)
          ) as never
        );
        return arr;
      }, []);

      const actionsValues = rowsAlphaTransform.map((v: any) =>
        v.assignedAction.map((x: any) => ({
          action: x,
          group: v.group.id,
        }))
      );

      const flatActions = actionsValues.flat();

      const actionsPromises = flatActions.map(
        (actions: { action: number; group: number }) => {
          return moveScenarioToGroup(
            arg.token,
            actions.action,
            actions.group,
            locale
          );
        }
      );

      const movingScenarioGrouping = await Promise.all(actionsPromises);
      store.dispatch(
        setVerifyAllIsCreated({
          ...verifyIfAllIsCreated,
          createdUsers: createdUsers.length >= 1 ? true : false,
          linkActions: movingScenarioGrouping.length >= 1 ? true : false,
          usersCreated: createdUsers.length,
        })
      );

      store.dispatch(setMultiLocsCreated(data.length));
      store.dispatch(setMultiLocationLoading(false));
      store.dispatch(setMultiLocationSuccess(true));

      return createdUsers;
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  }
);

export const fetchCountryNameMapbox = async (
  token: string,
  locale: string,
  longitude: number,
  latitude: number
): Promise<any> => {
  try {
    const res = await axios({
      method: "GET",
      url: `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${longitude}&latitude=${latitude}&access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
      headers: {
        "Accept-Language": `${locale}`,
        Authorization: `Bearer ${token}`,
      },
    });
    const { data } = res;

    return data.features.length > 0 ? data.features[0] : "unknown";
  } catch (error) {
    return error;
  }
};

export const createSingleLocation = async (
  token: string,
  locale: string,
  realmId: number,
  name: string,
  latitude: number,
  longitude: number,
  countryName?: string
): Promise<any> => {
  const res = await axios({
    method: "POST",
    url: `${API}/users/organization-geo-point`,
    data: {
      name,
      orgId: realmId,
      typeId: 1,
      latitude,
      longitude,
      altitude: 0,
      countryName,
    },
    headers: {
      Authorization: `Bearer ${token}`,
      "Accept-Language": `${locale}`,
    },
  });
  const { data } = res;

  return data;
};

export const createMultiLocationLocation = createAsyncThunk(
  "CreateMultiLocationReducer/createLocation",
  async (arg: { token: string; realmId: number }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { csvResults } = (store.getState() as RootState)
      .CreateLocationReducer;

    store.dispatch(setMultiLocationLoading(true));
    const objectToMultiLoc = csvResults.map(async (values) => {
      const countryName = await fetchCountryNameMapbox(
        arg.token,
        locale,
        parseFloat(values.longitude as any),
        parseFloat(values.latitude as any)
      ).then((values) =>
        values.properties ? values.properties.context.country.name : values
      );

      return createSingleLocation(
        arg.token,
        locale,
        arg.realmId,
        values.siteName,
        values.latitude,
        values.longitude,
        countryName
      );
    });
    const locationsData = await Promise.all(objectToMultiLoc);

    if (locationsData.filter((v) => v.countryName === undefined).length === 0) {
      try {
        store.dispatch(setMultiLocsCreated(locationsData.length));
        store.dispatch(setMultiLocationLoading(false));
        store.dispatch(setMultiLocationSuccess(true));
        return locationsData;
      } catch (error: any) {
        store.dispatch(setMultiLocationLoading(false));
        return store.dispatch(setMultiLocError(error?.response?.data?.message));
      }
    } else {
      return store.dispatch(setMultiLocError("No country name"));
    }
  }
);

export const createMultiUsersWithActions = createAsyncThunk(
  "createMultiUsersWithActions/createLocation",
  async (arg: { adminId: string; token: string; realmId: number }, store) => {
    const { rowsAlpha, verifyIfAllIsCreated } = (store.getState() as RootState)
      .CreateUsersReducer;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    store.dispatch(setMultiLocationLoading(true));

    try {
      const usersPromises = rowsAlpha.map((usersConfigs) => {
        const manageSiteAssign = () => {
          if (usersConfigs.nomadUser) {
            return undefined;
          } else {
            return usersConfigs.assignedSite.id;
          }
        };

        return store.dispatch(
          userCreation({
            token: arg.token,
            username: usersConfigs.username,
            password: generatePassword(),
            associatedGeoPointId: manageSiteAssign() as number,
            adminId: arg.adminId,
          })
        );
      });

      const createdUsers = await Promise.all(usersPromises);
      const res = createdUsers.map((v) => v.payload);

      const rowsAlphaTransform = rowsAlpha.reduce((arr, e) => {
        arr.push(
          Object.assign(
            {} as never,
            e,
            res.find((a) => a.username === e.username)
          )
        );
        return arr;
      }, []);

      const actionsValues = rowsAlphaTransform.map((v: any) =>
        v.assignedAction.map((x: any) => ({
          action: x,
          group: v.group.id,
        }))
      );

      const flatActions = actionsValues.flat();

      const actionsPromises = flatActions.map(
        (actions: { action: number; group: number }) => {
          return moveScenarioToGroup(
            arg.token,
            actions.action,
            actions.group,
            locale
          );
        }
      );

      const movingScenarioGrouping = await Promise.all(actionsPromises);

      store.dispatch(
        setVerifyAllIsCreated({
          ...verifyIfAllIsCreated,
          createdUsers: createdUsers.length >= 1 ? true : false,
          linkActions: movingScenarioGrouping.length >= 1 ? true : false,
          usersCreated: createdUsers.length,
        })
      );

      store.dispatch(setUsersCreated(res));
      store.dispatch(setMultiLocationLoading(false));
    } catch (error: any) {
      store.dispatch(setMultiLocationLoading(false));
      return store.dispatch(setMultiLocError(error?.response?.data?.message));
    }
  }
);

export const fetchLocationsGroups = createAsyncThunk(
  "LocationsReducer/fetchLocationsGroups",
  async (token: string, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    try {
      const response = await axios.get(
        `${API}/users/organization-group?typeId=112&$eager=[memberships.[user,geoPoint]]&$limit=10000`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { data } = response;
      const filterGroups = data.data.filter(
        (group: any) => group.typeId === 112 && !group.isAutoGenerated
      );

      const newGroupObject = filterGroups.map((g: any) => ({
        createdAt: g.createdAt,
        deletedAt: g.deletedAt,
        groupTemplateId: g.groupTemplateId,
        id: g.id,
        isAutoGenerated: g.isAutoGenerated,
        name: g.name,
        orgId: g.orgId,
        typeId: g.typeId,
        updatedAt: g.updatedAt,
        locations: g.memberships
          .filter((memberships: any) => memberships.geoPoint !== null)
          .map((d: any) => d.geoPointId),
      }));

      return store.fulfillWithValue(newGroupObject);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  }
);

export const moveSiteTogroup = async (
  token: string,
  groupId: number,
  geoPointId: number,
  locale: string,
  orgId: number
): Promise<any> => {
  const res = await axios({
    method: "POST",
    url: `${API}/users/organization-group-membership`,
    data: {
      groupId: groupId,
      geoPointId: geoPointId,
      isActive: true,
      membershipType: 0, //to change later
    },
    headers: {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    },
  });

  const { data } = res;
  return data;
};

export const moveSiteTogroup2 = async (
  token: string,
  groupId: number,
  geoPointId: number,
  locale: string,
  membershipType: number
): Promise<any> => {
  const res = await axios({
    method: "POST",
    url: `${API}/users/organization-group-membership`,
    data: {
      groupId: groupId,
      geoPointId: geoPointId,
      isActive: true,
      membershipType,
    },
    headers: {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    },
  });

  const { data } = res;
  return data;
};

export const moveSitesTogroup = createAsyncThunk(
  "LocationsReducer/moveSitesTogroup",
  async (
    arg: {
      token: string;
    },
    store
  ) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { orgId } = (store.getState() as RootState).Authentication;
    const { createdGroups } = (store.getState() as RootState).LocationsReducer;
    const { locationsSelectedAutoComplete, groupSelectedToMoveLocation } =
      createdGroups;

    try {
      const locationPomises = locationsSelectedAutoComplete.valuesSelected.map(
        (locations) => {
          return moveSiteTogroup(
            arg.token,
            groupSelectedToMoveLocation as any,
            locations.id as number,
            locale,
            orgId as number
          );
        }
      );
      const move = await Promise.all(locationPomises);

      if (move.length >= 1) {
        store.dispatch(
          setCreatedGroupValues({
            ...createdGroups,
            isLoading: false,
            errorCreateGroup: "",
            groupName: "",
            isCreatingGroup: false,
            typeOfGroup: "",
            groupSelectedToMoveLocation: "",
            isCreatedGroupSuccess: true,
            locationsSelectedAutoComplete: {
              allValuesSelected: false,
              valuesSelected: [],
            },
          })
        );
        store.dispatch(fetchLocations(arg.token));
        store.dispatch(fetchLocationsGroups(arg.token));
      }
    } catch (error: any) {
      store.dispatch(
        setCreatedGroupValues({
          ...createdGroups,
          errorCreateGroup: error?.response?.data?.message,
        })
      );
    }
  }
);

export const createLocationGroup = createAsyncThunk(
  "LocationsReducer/createLocationGroup",
  async (
    arg: {
      token: string;
    },
    store
  ) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { orgId } = (store.getState() as RootState).Authentication;
    const { createdGroups } = (store.getState() as RootState).LocationsReducer;

    const clusterTypeGroup = 112;

    const headers = {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${arg.token}`,
    };

    try {
      const response = await axios.post(
        `${API}/users/organization-group`,
        {
          name: createdGroups.groupName,
          typeId: clusterTypeGroup,
          orgId,
          groupTemplateId: 27,
        },
        { headers }
      );

      const { data } = response;

      if (data.id) {
        const locationsToMovePromise =
          createdGroups.locationsSelectedAutoComplete.valuesSelected.map(
            (locations) => {
              return moveSiteTogroup(
                arg.token,
                data.id,
                locations.id as number,
                locale,
                orgId as number
              );
            }
          );

        await Promise.all(locationsToMovePromise);
        store.dispatch(
          setCreatedGroupValues({
            ...createdGroups,
            isLoading: false,
            errorCreateGroup: "",
            groupName: "",
            isCreatingGroup: false,
            typeOfGroup: "",
            groupSelectedToMoveLocation: "",
            isCreatedGroupSuccess: true,
            locationsSelectedAutoComplete: {
              allValuesSelected: false,
              valuesSelected: [],
            },
          })
        );
        store.dispatch(fetchLocations(arg.token));
        store.dispatch(fetchLocationsGroups(arg.token));
      }

      return store.fulfillWithValue(data);
    } catch (error: any) {
      return store.dispatch(
        setCreatedGroupValues({
          ...createdGroups,
          errorCreateGroup: error?.response?.data?.message,
        })
      );
    }
  }
);

export const fetchLocations = createAsyncThunk(
  "LocationsReducer/fetchLocations",
  async (token: string, store) => {
    const clusterTypeGroup = 112;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const queryParams = `/users/organization-geo-point?$eager=[groupMemberships.group, type, names]&deletedAt=null&$limit=10000`;

    try {
      const response = await axios.get(`${API}${queryParams}`, {
        headers: {
          "Accept-Language": `${locale}`,
          Authorization: `Bearer ${token}`,
        },
      });
      const { data } = response;

      const locations = data.data.map((loc: OrgGeoPointData) => ({
        createdAt: loc.createdAt,
        updatedAt: loc.updatedAt,
        id: loc.id,
        name: loc.name,
        orgId: loc.orgId,
        altitude: loc.altitude,
        longitude: loc.longitude,
        latitude: loc.latitude,
        deletedAt: loc.deletedAt,
        typeId: loc.typeId,
        imageFileId: loc.imageFileId,
        primaryContactName: loc.primaryContactName,
        telephone: loc.telephone,
        email: loc.email,
        address: loc.address,
        notes: loc.notes,
        remoteId: loc.remoteId,
        siteLoginsUserId: loc.siteLoginsUserId,
        siteLoginUsernamePrimitive: loc.siteLoginUsernamePrimitive,
        countryName: loc.countryName,
        sitePassword: loc,
        hasActivity: loc.hasActivity,
        unAckNotifications: loc.unAckNotifications,
        locationGroups: loc?.groupMemberships
          ?.filter((group: any) => group.group.typeId === clusterTypeGroup)
          .map((result: any) => ({
            groupName: result.group.name,
            id: result.group.id,
            createdAt: result.createdAt,
            deletedAt: result.deletedAt,
            geoPointId: result.geoPointId,
          })),

        type: loc.type,
        names: loc.names,
        totalCapacity: loc.totalCapacity,
        shared: loc.shared ?? false,
      }));

      return store.fulfillWithValue(locations);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  }
);
